import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import WorkflowTriggerCard, {
  WorkflowAddTriggerCard,
} from './WorkflowTriggerCard';
import ConfirmDeleteTriggerModal from './ConfirmDeleteTriggerModal';
import WorkflowTriggerStatsModal from './WorkflowTriggerStatsModal';
import WorkflowTriggerStatsPopover from './WorkflowTriggerStatsPopover';
import { fetchTriggerEnrollmentStats } from '../../utils/workflowTriggerStatsApi';
import { WORKFLOW_CONNECTOR_LINE } from '../../utils/workflowCanvasViewportUtils';
import {
  buildProductionTriggerMergePathEntries,
  PRODUCTION_TRIGGER_CONNECTOR,
} from '../../utils/workflowConnectorPaths';
import {
  WORKFLOW_MAIN_COLUMN_WIDTH,
  getCardCenterX,
  getMainCenterInRowCoords,
} from '../../utils/workflowCardAnchors';
import { resolveTriggerConnectorLayout, isSameTriggerConnectorGeometry } from '../../utils/workflowTriggerConnectorLayout';
import {
  duplicateWorkflowTrigger,
  EMPTY_TRIGGER_STATS,
  getTriggerConditionText,
  normalizeWorkflowTriggers,
} from '../../utils/workflowTriggerUtils';

const MERGE_STROKE = WORKFLOW_CONNECTOR_LINE.color;
const MERGE_TRUNK_Y = PRODUCTION_TRIGGER_CONNECTOR.mergeY;
const MERGE_DROP_Y = PRODUCTION_TRIGGER_CONNECTOR.insertPlusTopY;

function connectorLayoutsEqual(previous, next) {
  if (previous === next) return true;
  if (!previous || !next) return false;

  return previous.visualRowWidth === next.visualRowWidth
    && previous.rowWidth === next.rowWidth
    && previous.connectorRowOffset === next.connectorRowOffset
    && previous.triggerMergeCenterX === next.triggerMergeCenterX
    && previous.triggerRowVisualCenterX === next.triggerRowVisualCenterX
    && previous.actionLaneCenterX === next.actionLaneCenterX
    && previous.mainLaneCenterXRow === next.mainLaneCenterXRow
    && previous.includeAddTileConnector === next.includeAddTileConnector
    && previous.hasMerge === next.hasMerge
    && previous.triggerCenters?.length === next.triggerCenters?.length
    && (previous.triggerCenters || []).every((value, index) => value === next.triggerCenters[index])
    && (previous.branchCenters || []).every((value, index) => value === next.branchCenters[index]);
}

function TriggerMergeConnectors({ layout }) {
  if (!layout) return null;

  const {
    triggerCenters,
    triggerRowVisualCenterX,
    actionLaneCenterX,
    triggerMergeCenterX,
    triggerTrunkEndX,
    trunkEndRowX,
    rowWidth,
    connectorRowOffset = 0,
    includeAddTileConnector,
  } = layout;

  const localActionLaneCenterX = getCardCenterX(WORKFLOW_MAIN_COLUMN_WIDTH);
  const triggerMergeCenterGraphX = triggerMergeCenterX
    ?? triggerRowVisualCenterX
    ?? layout.connectorCenterX;
  const visibleRowCenterX = triggerRowVisualCenterX
    ?? layout.connectorCenterX
    ?? triggerMergeCenterGraphX;
  const trunkEndX = trunkEndRowX ?? getMainCenterInRowCoords(rowWidth);

  if (visibleRowCenterX == null && layout.mainLaneCenterX == null) return null;

  const pathEntries = buildProductionTriggerMergePathEntries({
    triggerCenters,
    addTileCenterX: layout.addTileCenterX,
    includeAddTileConnector,
    visibleRowCenterX,
    actionLaneCenterX: trunkEndX,
    mergeY: MERGE_TRUNK_Y,
    insertPlusTopY: MERGE_DROP_Y,
    radius: PRODUCTION_TRIGGER_CONNECTOR.radius,
    centerRadius: PRODUCTION_TRIGGER_CONNECTOR.centerRadius,
  });

  return (
    <div
      className="relative mx-auto overflow-visible"
      style={{
        width: rowWidth,
        height: MERGE_DROP_Y,
        marginLeft: connectorRowOffset,
      }}
      data-workflow-connector
      data-workflow-trigger-connector="true"
      data-workflow-main-center-x={localActionLaneCenterX}
      data-workflow-action-lane-center-x={localActionLaneCenterX}
      data-workflow-main-global-center-x={triggerMergeCenterGraphX}
      data-workflow-trigger-merge-center-x={triggerMergeCenterGraphX}
      data-workflow-trigger-trunk-end-x={triggerMergeCenterGraphX}
      data-workflow-trigger-row-visual-center-x={visibleRowCenterX}
      data-workflow-branch-merge-point={`${visibleRowCenterX},${MERGE_TRUNK_Y}`}
      data-workflow-trigger-lane-center-x={trunkEndX}
      data-workflow-trigger-lane-center-y={MERGE_DROP_Y}
      data-workflow-merge-point={`${trunkEndX},${MERGE_DROP_Y}`}
      data-workflow-connector-source={layout.sourceType}
      data-workflow-include-add-tile-connector={includeAddTileConnector ? 'true' : 'false'}
      data-testid="workflow-trigger-connector"
    >
      <svg
        width={rowWidth}
        height={MERGE_DROP_Y}
        className="pointer-events-none absolute left-0 top-0 overflow-visible"
        aria-hidden="true"
        data-workflow-connector-svg
        data-workflow-trigger-connector-svg="true"
        data-testid="workflow-trigger-connector-svg"
      >
        {pathEntries.map((entry, index) => (
          <path
            key={`${entry.pathType}-${entry.centerX}-${index}`}
            d={entry.d}
            fill="none"
            stroke={MERGE_STROKE}
            strokeWidth={WORKFLOW_CONNECTOR_LINE.width}
            strokeLinecap="round"
            strokeLinejoin="round"
            data-workflow-connector-path={entry.pathType}
            data-workflow-trigger-connector-path={entry.pathType}
            data-workflow-card-center-x={
              entry.pathType === 'trigger-branch'
                || entry.pathType === 'add-trigger-branch'
                || entry.pathType === 'single'
                ? entry.centerX
                : undefined
            }
            data-workflow-node-anchor={
              entry.pathType === 'trigger-branch' || entry.pathType === 'add-trigger-branch'
                ? `${entry.centerX},0`
                : undefined
            }
            data-workflow-branch-merge-anchor={
              entry.pathType === 'trigger-branch' || entry.pathType === 'add-trigger-branch'
                ? `${visibleRowCenterX},${MERGE_TRUNK_Y}`
                : undefined
            }
            data-workflow-trunk-start={
              entry.pathType === 'merge-trunk'
                ? `${visibleRowCenterX},${MERGE_TRUNK_Y}`
                : undefined
            }
            data-workflow-connector-anchor-bottom={
              entry.pathType === 'merge-trunk' || entry.pathType === 'single'
                ? `${trunkEndX},${MERGE_DROP_Y}`
                : undefined
            }
          />
        ))}
      </svg>
    </div>
  );
}

const WorkflowTriggerCanvasSection = ({
  workflow,
  readOnly = false,
  workflowId,
  token,
  agencyId,
  onTriggersChange,
  onAddTrigger,
  onEditTrigger,
  onConnectorLayoutChange,
  onStatsOpen,
}) => {
  const triggers = useMemo(() => normalizeWorkflowTriggers(workflow), [workflow]);
  const [statsTriggerId, setStatsTriggerId] = useState(null);
  const [statsPopoverOpen, setStatsPopoverOpen] = useState(false);
  const [statsAnchorEl, setStatsAnchorEl] = useState(null);
  const [statsModalOpen, setStatsModalOpen] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsData, setStatsData] = useState(EMPTY_TRIGGER_STATS);
  const [pendingDeleteTrigger, setPendingDeleteTrigger] = useState(null);
  const [connectorLayout, setConnectorLayout] = useState(null);

  const graphCanvasRef = useRef(null);
  const triggerRowRef = useRef(null);
  const rowContainerRef = useRef(null);
  const addTriggerTileRef = useRef(null);
  const triggerRefs = useRef(new Map());
  const connectorLayoutRef = useRef(null);

  const setTriggerRef = useCallback((triggerId, node) => {
    if (node) {
      triggerRefs.current.set(triggerId, node);
    } else {
      triggerRefs.current.delete(triggerId);
    }
  }, []);

  const measureConnectorLayout = useCallback(() => {
    const triggerElements = triggers
      .map((trigger) => triggerRefs.current.get(trigger.id))
      .filter(Boolean);

    const expectAddTile = triggers.length > 0 && !readOnly;

    const layout = resolveTriggerConnectorLayout({
      rowElement: triggers.length === 0 ? rowContainerRef.current : triggerRowRef.current,
      triggerElements,
      addTileElement: triggers.length === 0 || expectAddTile ? addTriggerTileRef.current : null,
      triggerCount: triggers.length,
      expectAddTile,
    });

    if (!layout) return;

    if (
      connectorLayoutRef.current
      && isSameTriggerConnectorGeometry(connectorLayoutRef.current, layout)
    ) {
      return;
    }

    setConnectorLayout((previous) => (
      connectorLayoutsEqual(previous, layout) ? previous : layout
    ));

    if (!connectorLayoutsEqual(connectorLayoutRef.current, layout)) {
      connectorLayoutRef.current = layout;
      onConnectorLayoutChange?.(layout);
    }
  }, [onConnectorLayoutChange, readOnly, triggers]);

  useLayoutEffect(() => {
    measureConnectorLayout();
  }, [measureConnectorLayout]);

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(() => {
      measureConnectorLayout();
    });

    if (triggerRowRef.current) {
      observer.observe(triggerRowRef.current);
    }

    if (rowContainerRef.current) {
      observer.observe(rowContainerRef.current);
    }

    triggers.forEach((trigger) => {
      const element = triggerRefs.current.get(trigger.id);
      if (element) observer.observe(element);
    });

    if (addTriggerTileRef.current) {
      observer.observe(addTriggerTileRef.current);
    }

    return () => observer.disconnect();
  }, [measureConnectorLayout, readOnly, triggers]);

  const activeStatsTrigger = useMemo(
    () => triggers.find((trigger) => trigger.id === statsTriggerId) || null,
    [triggers, statsTriggerId],
  );

  const loadPopoverStats = useCallback(async (triggerId) => {
    if (!triggerId || !token || !workflowId || workflowId === 'new') {
      setStatsData(EMPTY_TRIGGER_STATS);
      return;
    }
    setStatsLoading(true);
    try {
      const data = await fetchTriggerEnrollmentStats({
        token,
        workflowId,
        agencyId,
        triggerId,
        page: 1,
        pageSize: 1,
      });
      setStatsData(data);
    } finally {
      setStatsLoading(false);
    }
  }, [token, workflowId, agencyId]);

  useEffect(() => {
    if (!statsPopoverOpen || !statsTriggerId) return;
    loadPopoverStats(statsTriggerId);
  }, [statsPopoverOpen, statsTriggerId, loadPopoverStats]);

  const handleCopyTrigger = (trigger) => {
    const next = [...triggers, duplicateWorkflowTrigger(trigger)];
    onTriggersChange?.(next);
  };

  const handleDeleteTrigger = (trigger) => {
    const next = triggers.filter((item) => item.id !== trigger.id);
    onTriggersChange?.(next);
    if (statsTriggerId === trigger.id) {
      setStatsPopoverOpen(false);
      setStatsTriggerId(null);
      setStatsAnchorEl(null);
    }
  };

  const requestDeleteTrigger = (trigger) => {
    closeStatsPopover();
    setPendingDeleteTrigger(trigger);
  };

  const confirmDeleteTrigger = () => {
    if (!pendingDeleteTrigger) return;
    handleDeleteTrigger(pendingDeleteTrigger);
    setPendingDeleteTrigger(null);
  };

  const handleEditTrigger = (trigger) => {
    closeStatsPopover();
    onEditTrigger?.(trigger);
  };

  const openStatsPopover = (trigger, anchorEl) => {
    if (statsPopoverOpen && statsTriggerId === trigger.id) {
      closeStatsPopover();
      return;
    }
    setStatsTriggerId(trigger.id);
    setStatsAnchorEl(anchorEl || null);
    setStatsPopoverOpen(true);
    onStatsOpen?.(trigger);
  };

  const closeStatsPopover = () => {
    setStatsPopoverOpen(false);
    setStatsAnchorEl(null);
  };

  const mainColumnWidth = WORKFLOW_MAIN_COLUMN_WIDTH;
  const graphWidth = Math.max(
    mainColumnWidth,
    connectorLayout?.visualRowWidth ?? connectorLayout?.rowWidth ?? mainColumnWidth,
  );
  const graphCenterX = connectorLayout?.triggerMergeCenterX
    ?? connectorLayout?.triggerRowVisualCenterX
    ?? connectorLayout?.mainLaneCenterX
    ?? mainColumnWidth / 2;

  return (
    <div
      className="mb-0 w-full max-w-none overflow-visible"
      data-testid="workflow-trigger-canvas-section"
    >
    <div
      ref={graphCanvasRef}
      className="relative mx-auto shrink-0 overflow-visible"
      style={{ width: graphWidth }}
      data-workflow-graph-canvas
      data-workflow-canvas-node
        data-workflow-main-column
        data-workflow-graph-canvas-width={graphWidth}
        data-workflow-row-width={graphWidth}
        data-workflow-main-center-x={graphCenterX}
        data-testid="workflow-trigger-row"
      >
        {triggers.length === 0 ? (
          <div
            ref={rowContainerRef}
            className="flex w-full flex-col items-stretch"
            data-testid="workflow-trigger-group"
          >
            <div
              ref={addTriggerTileRef}
              className="trigger-card-shell relative mx-auto shrink-0"
              data-testid="add-trigger-tile"
              data-workflow-graph-bounds
              data-workflow-trigger-row-bounds
            >
              <WorkflowAddTriggerCard readOnly={readOnly} onClick={onAddTrigger} />
            </div>
            <TriggerMergeConnectors layout={connectorLayout} />
          </div>
        ) : (
          <div
            ref={rowContainerRef}
            className="flex w-full flex-col"
            data-testid="workflow-trigger-row-container"
          >
            <div
              ref={triggerRowRef}
              className="mx-auto flex w-max max-w-none flex-nowrap items-start gap-4"
              data-testid="workflow-trigger-group"
              data-workflow-graph-bounds
              data-workflow-trigger-row-bounds
            >
              {triggers.map((trigger) => (
                <div
                  key={trigger.id}
                  ref={(node) => setTriggerRef(trigger.id, node)}
                  className="trigger-card-shell shrink-0"
                  data-workflow-trigger-card-shell="true"
                  data-workflow-node-anchor={trigger.id}
                >
                  <WorkflowTriggerCard
                    trigger={trigger}
                    readOnly={readOnly}
                    onClick={handleEditTrigger}
                    onCopy={readOnly ? undefined : handleCopyTrigger}
                    onDelete={readOnly ? undefined : requestDeleteTrigger}
                    onStatsHover={openStatsPopover}
                    onStatsLeave={() => {}}
                    onStatsClick={openStatsPopover}
                    statsOpen={statsPopoverOpen && statsTriggerId === trigger.id}
                  />
                </div>
              ))}

              {!readOnly ? (
                <div
                  ref={addTriggerTileRef}
                  className="trigger-card-shell shrink-0"
                  data-workflow-add-trigger-tile="true"
                  data-testid="workflow-add-trigger-tile"
                  data-workflow-row-control
                >
                  <WorkflowAddTriggerCard onClick={onAddTrigger} />
                </div>
              ) : null}
            </div>
            <TriggerMergeConnectors layout={connectorLayout} />
          </div>
        )}
      </div>

      <WorkflowTriggerStatsPopover
        isOpen={statsPopoverOpen}
        onClose={closeStatsPopover}
        anchorEl={statsAnchorEl}
        stats={statsData}
        loading={statsLoading}
        conditionText={getTriggerConditionText(activeStatsTrigger)}
        onViewDetailed={() => {
          closeStatsPopover();
          setStatsModalOpen(true);
        }}
      />

      {statsModalOpen ? (
        <WorkflowTriggerStatsModal
          isOpen={statsModalOpen}
          onClose={() => setStatsModalOpen(false)}
          trigger={activeStatsTrigger}
          token={token}
          workflowId={workflowId}
          agencyId={agencyId}
        />
      ) : null}

      <ConfirmDeleteTriggerModal
        isOpen={Boolean(pendingDeleteTrigger)}
        onCancel={() => setPendingDeleteTrigger(null)}
        onConfirm={confirmDeleteTrigger}
      />
    </div>
  );
};

export default WorkflowTriggerCanvasSection;
